import { useMemo } from 'react';
import { CarrierLogo } from '../../../brite-viewer/components/benefits/carrier-logo';
import { Box } from '../../../../shared-components/Box';
import { Text } from '../../../../shared-components';
import { CostGroup } from './CostGroup';
import { Post, Pre, SimpleFieldValue } from './SimpleFieldValue';
import { getFieldsAndGroups, getHasCost, productHasIncludedPrograms } from './utils';
import { IncludedPrograms } from './IncludedPrograms';
import { CustomDetails } from './CustomDetails';
import { Conditions } from './Conditions';
import { container } from '../../../../shared-components/shared-styles';
import { Masonry } from '../../../../shared-components/masonry/Masonry';

export const PDFSummary = ({ data }) => {
  const {
    product,
    layout,
    includedPrograms,
    meta: { networks },
  } = data;

  const coreData = product?.Type === 'insurance_plan' ? networks.core : product;
  const isCustom = product?.Type === 'custom';

  const { fields, groups, detail } = useMemo(() => {
    return getFieldsAndGroups(product, layout);
  }, [product?.ID, layout?.Sections?.length]);

  const hasEmbeddedDisclaimer = product?.Details?.InNetworkPlanDesign?.SingleEmbeddedDeductibleOOPM || false;
  const disclaimer = product?.Disclaimer;

  const hasCostSection = useMemo(() => getHasCost(data), [data?.layout?.Sections]);
  const hasIncludedPrograms = productHasIncludedPrograms(product, includedPrograms);
  const customDetailsList = product?.TitleDescriptions?.filter(({ Autogenerated }) => !Autogenerated);
  const conditions = product?.Details?.ConditionPercentages?.filter((item) => item.Name && item.Percentage) || [];
  const hasConditions = product?.Type === 'critical_illness' && conditions?.length;

  return (
    <Box
      css={`
        width: 100%;
        border-radius: 8px;
        ${container.box}
        box-sizing: border-box;
        padding: 24px;
      `}
    >
      <Box
        flex="space-between middle"
        css={`
          margin-bottom: 16px;
        `}
      >
        <CarrierLogo carrierID={coreData?.ProviderID} planCarrierName={coreData?.ProviderName} />
        <Box
          css={`
            display: grid;
            gap: 8px;
          `}
        >
          <Text h4>{coreData?.ProductName || 'Benefit Summary'}</Text>
          {isCustom ? <Text>{product?.Description}</Text> : null}
          {product?.Details?.NetworkName ? (
            <Text
              css={`
                text-align: right;
              `}
            >
              {product?.Details?.NetworkName}{' '}
              {product?.Details?.MultiCarrierNetworkName && coreData?.Details?.IsMultiCarrier
                ? `| ${product?.Details?.MultiCarrierNetworkName}`
                : ''}
            </Text>
          ) : null}
        </Box>
      </Box>

      <Masonry>
        {hasCostSection ? <CostGroup data={data} key="cost" /> : null}

        {Object.keys(groups || {})?.map((groupKey) => (
          <Masonry.Group key={`fields-${groupKey}`}>
            <Masonry.Group.Title value={fields[groupKey]?.DisplayValue} />
            <Masonry.Group.Content>
              {groups[groupKey]?.map((key) => {
                const field = fields[key];
                return (
                  <Masonry.Group.Item
                    key={key}
                    flex=""
                    css={`
                      :not(:last-child) {
                        border-bottom: 1px solid var(--accent-color-4);
                      }
                    `}
                  >
                    <Pre field={field} />
                    <Box flex="space-between">
                      <Text label>{field?.DisplayValue}</Text>
                      <SimpleFieldValue field={field} />
                    </Box>
                    <Post field={field} />
                  </Masonry.Group.Item>
                );
              })}
            </Masonry.Group.Content>
          </Masonry.Group>
        ))}

        {product?.Type !== 'insurance_plan' ? (
          <Masonry.Group key="plan-details">
            <Masonry.Group.Title value="Plan Details" />
            <Masonry.Group.Content>
              {detail?.map((key) => {
                const field = fields[key];
                return (
                  <Masonry.Group.Item
                    key={key}
                    flex=""
                    css={`
                      :not(:last-child) {
                        border-bottom: 1px solid var(--accent-color-4);
                      }
                    `}
                  >
                    <Pre field={field} />
                    <Box flex="space-between">
                      <Text label>{field?.DisplayValue}</Text>
                      <SimpleFieldValue field={field} />
                    </Box>
                    <Post field={field} />
                  </Masonry.Group.Item>
                );
              })}
            </Masonry.Group.Content>
          </Masonry.Group>
        ) : null}

        {hasIncludedPrograms ? (
          <IncludedPrograms key="included-programs" includedPrograms={includedPrograms} product={product} />
        ) : null}
        {customDetailsList?.length ? (
          <CustomDetails key="custom-details" customDetailsList={customDetailsList} />
        ) : null}
        {hasConditions ? <Conditions key="conditions" conditions={conditions} /> : null}
      </Masonry>
      {hasEmbeddedDisclaimer ? (
        <Text
          css={`
            margin: 16px;
            font-size: 0.9em;
          `}
        >
          * All dependents deductibles & OOPM are subject to the single deductible & OOPM.
        </Text>
      ) : null}
      {disclaimer ? (
        <Text body caption>
          {disclaimer}
        </Text>
      ) : null}
    </Box>
  );
};
